import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { geoEqualEarth, geoPath, geoGraticule10 } from "d3-geo";
import { feature } from "topojson-client";
import land110 from "world-atlas/land-110m.json";
import { ArrowUpRight, Flame, Globe2, X, TrendingUp } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines, EASE } from "@/components/Motion";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const W = 960;
const H = 500;
const projection = geoEqualEarth().scale(178).translate([W / 2, H / 2 + 10]);
const path = geoPath(projection);
const LAND = path(feature(land110, land110.objects.land));
const GRAT = path(geoGraticule10());
const KIND = { event: "Event", brief: "Brief", opportunity: "Opportunity" };

export default function PartnershipMap() {
  useSeo("Global Partnership Activity Map", "A live heat map of partnership and sponsorship activity across 84 major cities worldwide.", "/map");
  const [cities, setCities] = useState([]);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState("volume");
  useEffect(() => { api.get("/map").then((r) => setCities(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  const max = useMemo(() => Math.max(1, ...cities.map((c) => (mode === "heat" ? c.recent : c.count))), [cities, mode]);
  const city = cities.find((c) => c.key === sel);
  const active = useMemo(() => cities.filter((c) => c.count > 0), [cities]);

  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="map-page">
      <Nav />
      <section className="pt-32 pb-10 mx-auto max-w-7xl px-5 sm:px-8">
        <p className="kicker">Global partnership map</p>
        <MaskedLines as="h1" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight" lines={["Where the world", <em key="m" className="italic gold-text">is partnering.</em>]} />
        <div className="mt-8 inline-flex rounded-full border border-[#2A1240]/15 p-1" data-testid="map-mode-toggle">
          {[["volume", "Opportunities", Globe2], ["heat", "Heat map · growth", Flame]].map(([k, l, I]) => (
            <button key={k} onClick={() => setMode(k)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${mode === k ? "bg-[#2A1240] text-white font-semibold" : "text-slate-200"}`} data-testid={`map-mode-${k}`}><I className="h-4 w-4" />{l}</button>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-10">
        <div className="relative overflow-hidden rounded-3xl border border-[#2A1240]/10 bg-[#FFF0F7]">
          <div className="overflow-x-auto no-scrollbar" data-lenis-prevent>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[720px] lg:min-w-0 h-auto" role="img" aria-label="World map of partnership activity" data-testid="map-svg">
            <defs>
              <radialGradient id="heat"><stop offset="0%" stopColor="#B5157A" stopOpacity="0.9" /><stop offset="45%" stopColor="#E0218A" stopOpacity="0.45" /><stop offset="100%" stopColor="#E0218A" stopOpacity="0" /></radialGradient>
              <radialGradient id="glow"><stop offset="0%" stopColor="#E0218A" stopOpacity="0.55" /><stop offset="100%" stopColor="#E0218A" stopOpacity="0" /></radialGradient>
            </defs>
            <path d={GRAT} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            <path d={LAND} fill="#FBE9F3" stroke="rgba(224,33,138,0.25)" strokeWidth="0.6" />
            {cities.map((c, i) => {
              const [x, y] = projection([c.lon, c.lat]);
              const v = mode === "heat" ? c.recent : c.count;
              const isActive = sel === c.key;
              if (!c.count) {
                return (
                  <g key={c.key} transform={`translate(${x},${y})`} className="cursor-pointer group" onClick={() => setSel(c.key)} data-testid={`map-city-${c.key.replace(/\s/g, "-")}`}>
                    <circle r="7" fill="transparent" />
                    <circle r={isActive ? 3.5 : 2.2} fill="#1E3A8A" opacity="0.85" />
                    <text y="-6" textAnchor="middle" className={`fill-slate-300 transition-opacity ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} style={{ font: "10px 'Cabinet Grotesk', sans-serif" }}>{c.name}</text>
                  </g>
                );
              }
              const r = 4 + (v / max) * 14;
              return (
                <g key={c.key} transform={`translate(${x},${y})`} className="cursor-pointer" onClick={() => setSel(c.key)} data-testid={`map-city-${c.key.replace(/\s/g, "-")}`}>
                  {mode === "heat" ? <circle r={r * 3.2} fill="url(#heat)" opacity={v ? 1 : 0.25} />
                    : <motion.circle r={r * 2} fill="url(#glow)" animate={{ scale: [1, 1.35, 1] }} transition={{ duration: 3, repeat: Infinity, delay: i * 0.2 }} />}
                  <circle r={isActive ? r * 0.75 : r * 0.5} fill={mode === "heat" ? "#F2559E" : "#E0218A"} stroke="#FFF7FB" strokeWidth="1.2" />
                  <text y={i % 2 ? r + 14 : -r - 6} textAnchor="middle" className="fill-[#2A1240]" style={{ font: "500 11px 'Cabinet Grotesk', sans-serif" }}>{c.name} <tspan className="fill-[#8E2BC4]" style={{ font: "10px 'JetBrains Mono', monospace" }}>{mode === "heat" ? `${c.growth > 0 ? "+" : ""}${c.growth}%` : c.count.toLocaleString()}</tspan></text>
                </g>
              );
            })}
          </svg>
          </div>
          {!active.length && <p className="absolute inset-0 grid place-items-center text-slate-400 pointer-events-none" data-testid="map-empty">No located activity yet.</p>}
          <AnimatePresence>
            {city && (
              <motion.aside initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} transition={{ duration: 0.5, ease: EASE }}
                className={`lg:absolute lg:top-4 lg:bottom-4 lg:w-96 ${city.lon > 30 ? "lg:left-4" : "lg:right-4"} border-t lg:border border-[#2A1240]/10 lg:rounded-2xl bg-[#FFFFFF]/95 backdrop-blur-xl p-6 overflow-y-auto`} data-lenis-prevent data-testid="map-city-panel">
                <div className="flex items-start justify-between">
                  <div><p className="font-display text-3xl text-[#2A1240]">{city.name}</p><p className="mt-1 text-sm text-[#8E2BC4]">{city.count ? `${city.count.toLocaleString()} partnership${city.count === 1 ? "" : "s"} available` : "No partnerships listed yet"}</p></div>
                  <button onClick={() => setSel(null)} aria-label="Close" data-testid="map-panel-close"><X className="h-5 w-5 text-slate-300" /></button>
                </div>
                <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-300"><TrendingUp className="h-3.5 w-3.5 text-[#F2559E]" />{city.recent} new in the last 30 days · {city.growth > 0 ? "+" : ""}{city.growth}% vs prior 30</p>
                <div className="mt-5 divide-y divide-[#2A1240]/10">
                  {!city.items.length && <p className="py-4 text-sm text-slate-300" data-testid="map-city-empty">Be the first to post an opportunity, brief or event in {city.name}. <Link to="/app/network" className="text-[#8E2BC4] underline">Post one</Link></p>}
                  {city.items.map((it, i) => (
                    <Link key={i} to={it.link} className="group flex items-center justify-between gap-3 py-3" data-testid={`map-item-${i}`}>
                      <div className="min-w-0"><p className="text-[10px] uppercase tracking-[0.2em] text-[#8E2BC4]">{KIND[it.kind]}</p><p className="truncate text-sm text-[#2A1240] group-hover:text-[#8E2BC4]">{it.title}</p><p className="text-xs text-slate-400">{it.org} {it.value && `· ${it.value}`}</p></div>
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-[#E0218A]" />
                    </Link>
                  ))}
                </div>
              </motion.aside>
            )}
          </AnimatePresence>
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="map-city-list">
          {[...active].sort((a, b) => (mode === "heat" ? b.growth - a.growth : b.count - a.count)).map((c) => (
            <button key={c.key} onClick={() => setSel(c.key)} className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-colors ${sel === c.key ? "border-[#E0218A] bg-[#E0218A]/5" : "border-[#2A1240]/10 bg-[#FFFFFF] hover:border-[#E0218A]/40"}`} data-testid={`map-list-${c.key.replace(/\s/g, "-")}`}>
              <span className="font-display text-xl text-[#2A1240]">{c.name}</span>
              <span className="font-mono text-sm text-[#8E2BC4]">{mode === "heat" ? `${c.growth > 0 ? "+" : ""}${c.growth}%` : c.count.toLocaleString()}</span>
            </button>
          ))}
        </div>
        <div className="mt-8" data-testid="map-all-cities">
          <p className="kicker">All cities</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[...cities].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (
              <button key={c.key} onClick={() => setSel(c.key)} className={`rounded-full px-3 py-1.5 text-xs transition-colors ${sel === c.key ? "bg-[#E0218A] text-white font-bold" : c.count ? "border border-[#E0218A]/40 text-[#8E2BC4]" : "bg-[#2A1240]/5 text-slate-300 hover:bg-[#2A1240]/10"}`} data-testid={`map-chip-${c.key.replace(/\s/g, "-")}`}>{c.name}{c.count ? ` · ${c.count}` : ""}</button>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
